/**
 * src/components/student-dashboard-components/theme/ThemeContext.js
 *
 * React Component Module.
 *
 * Purpose: React UI module containing one or more components and local UI helpers.
 *
 * Data flow (high level):
 * - UI/Caller -> this module -> downstream helpers/services -> state/response.
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { themeConfig } from './themeConfig';

const ThemeContext = createContext();

/**
 * Use Theme.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */


export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * Theme Provider.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */


export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState('default');
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
        /**
     * Check Mobile.
     *
     * React component or UI helper used to render and manage local UI interactions.
     */
     const checkMobile = () => {
      // Keep 768px in the tablet/desktop layout. Tailwind's `md` breakpoint also
      // starts at 768px, so the previous inclusive boundary rendered mixed shells.
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const theme = {
    ...themeConfig,
    currentTheme,
    isMobile,
    setTheme: setCurrentTheme
  };

  useEffect(() => {
    const root = document.documentElement;
    const colors = themeConfig.colors;
    
    root.style.setProperty('--primary-blue', colors.primary.blue);
    root.style.setProperty('--secondary-blue', colors.primary.lightBlue);
    root.style.setProperty('--accent-blue', colors.primary.skyBlue);
    root.style.setProperty('--accent-yellow', colors.accent.yellow);
    root.style.setProperty('--neutral-white', colors.neutral.white);
    root.style.setProperty('--neutral-light', colors.neutral.light);
    root.style.setProperty('--neutral-medium', colors.neutral.medium);
    root.style.setProperty('--neutral-dark', colors.neutral.dark);
    root.style.setProperty('--success', colors.semantic.success);
    root.style.setProperty('--warning', colors.semantic.warning);
    root.style.setProperty('--error', colors.semantic.error);
    root.style.setProperty('--info', colors.semantic.info);
  }, [currentTheme]);

  return (
    <ThemeContext.Provider value={theme}>
      {children}
    </ThemeContext.Provider>
  );
};
